import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getItem, setItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import styles from "./invoice.module.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../../../component/layout";
import {
  getInvoice,
  getCompanyInformationURL,
  getLevyURL,
  getBankDetailsForLevyURL,
  getBillerAddressForLevyURL,
  getFacilityPaymentDetailsURL,
} from "../../../../../utils/urls";
import { useFacilityStore } from "../../../../../app/z-store/store";
import InvoiceActionsDropdown from "./InvoiceActionsDropdown";
import EtimsQRCode from "../../../../common/EtimsQRCode";

const InvoicePage = () => {
  // State management
  const routerLocation = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);
  const [facilityLogo, setFacilityLogo] = useState(
    () => localStorage.getItem("selectedFacilityLogo") || "",
  );
  const [bankDetails, setBankDetails] = useState(null);
  const [billerAddress, setBillerAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [contractDetails, setContractDetails] = useState(null);

  // Individual loading states
  const [billerAddressLoading, setBillerAddressLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);

  // Hooks
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);

  // ─── Voided helper ───────────────────────────────────────────────────────────
  const isVoided = invoice?.status === "Void";

  // Check if all payment data has loaded
  const allPaymentDataLoaded =
    !contractLoading &&
    !billerAddressLoading &&
    !bankDetailsLoading &&
    !paymentSettingsLoading;

  // Fetch data on component mount
  useEffect(() => {
    fetchInvoiceData();
    getFacilityLogo();
  }, [invoiceId, facilityId]);

  // Fetch payment details when invoice is loaded
  useEffect(() => {
    if (invoice && invoice.whatFor) {
      if (
        invoice.whatFor.invoiceType === "Contract" &&
        invoice.whatFor.description
      ) {
        fetchPaymentDetailsForContract(invoice.whatFor.description);
      }
    }
  }, [invoice]);

  const getFacilityLogo = async () => {
    try {
      const facilityFromState = routerLocation.state?.facility;
      if (facilityFromState?.logo) {
        await setItem("selectedFacilityLogo", facilityFromState.logo);
        setFacilityLogo(facilityFromState.logo);
      } else {
        const storedLogo = await getItem("selectedFacilityLogo");
        setFacilityLogo(storedLogo || "");
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      const fallbackLogo = localStorage.getItem("selectedFacilityLogo");
      setFacilityLogo(fallbackLogo || "");
    }
  };

  // Fetch invoice data
  const fetchInvoiceData = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getInvoice}/${facilityId}/${invoiceId}`,
        "GET",
      );

      if (response.success) {
        const invoiceData = response.data?.data || response.data;
        setInvoice(invoiceData);
      } else {
        toastify(response.message || "Failed to fetch invoice", "error");
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toastify("Error fetching invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  const extractResponseData = (response) => {
    if (!response) return null;
    if (response.success && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    }
    return response;
  };

  const fetchLevyById = async (levyId) => {
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getLevyURL}/${facilityId}/${levyId}`,
        "GET",
      );
      if (response.success) {
        return extractResponseData(response);
      }
      return null;
    } catch (error) {
      console.error("Error fetching levy:", error);
      return null;
    }
  };

  const fetchPaymentDetailsForContract = async (contractId) => {
    const facilityId = await getItem("selectedFacilityId");
    try {
      setContractLoading(true);
      setBillerAddressLoading(true);
      setBankDetailsLoading(true);
      setPaymentSettingsLoading(true);

      setBankDetails(null);
      setBillerAddress(null);
      setPaymentSettings(null);
      setPaymentMethod(null);

      const contractResponse = await makeRequest2(
        `/api/app/levy_management/get_contract/${facilityId}/${contractId}`,
        "GET",
      );

      if (!contractResponse.success) {
        toastify("Failed to fetch contract details", "error");
        return;
      }

      const contractData = extractResponseData(contractResponse);
      if (!contractData) {
        toastify("No contract data found", "error");
        return;
      }

      setContractDetails(contractData);

      if (contractData && contractData.levyId) {
        let levy = null;

        if (
          typeof contractData.levyId === "object" &&
          contractData.levyId !== null &&
          contractData.levyId._id
        ) {
          levy = contractData.levyId;
        } else if (
          typeof contractData.levyId === "string" ||
          (typeof contractData.levyId === "object" && contractData.levyId.$oid)
        ) {
          const levyIdToFetch =
            typeof contractData.levyId === "string"
              ? contractData.levyId
              : contractData.levyId.$oid;
          levy = await fetchLevyById(levyIdToFetch);
        }

        if (levy) {
          await processLevyPaymentData(levy);
        } else {
          setBillerAddressLoading(false);
          setBankDetailsLoading(false);
          setPaymentSettingsLoading(false);
        }
      } else {
        setBillerAddressLoading(false);
        setBankDetailsLoading(false);
        setPaymentSettingsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching contract details:", error);
      toastify("Error fetching contract details", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    } finally {
      setContractLoading(false);
    }
  };

  const processLevyPaymentData = async (levy) => {
    try {
      const promises = [];

      if (levy.billerAddressId) {
        promises.push(fetchBillerAddress(levy.billerAddressId));
      } else {
        setBillerAddressLoading(false);
      }

      if (levy.bankPayment && levy.bankAccountId) {
        promises.push(fetchBankDetails(levy.bankAccountId));
      } else {
        setBankDetailsLoading(false);
      }

      if (levy.mobilePayment && levy.paymentMethodId) {
        promises.push(fetchMobilePaymentDetails(levy.paymentMethodId));
      } else {
        setPaymentSettingsLoading(false);
      }

      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }
    } catch (error) {
      console.error("Error processing payment configuration:", error);
      toastify("Error processing payment configuration", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  const fetchBankDetails = async (bankAccountId) => {
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`,
        "GET",
      );
      if (response.success) {
        setBankDetails(extractResponseData(response));
        setPaymentMethod("bank");
      } else {
        toastify("Failed to fetch bank details", "error");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toastify("Error fetching bank details", "error");
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const fetchMobilePaymentDetails = async (paymentMethodId) => {
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getFacilityPaymentDetailsURL}/${facilityId}/${paymentMethodId}`,
        "GET",
      );
      if (response.success) {
        setPaymentSettings(extractResponseData(response));
        setPaymentMethod("mobile");
      }
    } catch (error) {
      console.error("Error fetching mobile payment details:", error);
      toastify("Error fetching mobile payment details", "error");
    } finally {
      setPaymentSettingsLoading(false);
    }
  };

  const fetchBillerAddress = async (billerAddressId) => {
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`,
        "GET",
      );
      if (response.success) {
        setBillerAddress(extractResponseData(response));
      } else {
        toastify("Failed to fetch biller address", "error");
      }
    } catch (error) {
      console.error("Error fetching biller address:", error);
      toastify("Error fetching biller address", "error");
    } finally {
      setBillerAddressLoading(false);
    }
  };

  const markInvoiceAsRead = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getInvoice}/${facilityId}/${invoiceId}/markAsRead`,
        "POST",
      );
      if (response.success) {
        setInvoice((prev) => ({
          ...prev,
          viewStatus: { ...prev.viewStatus, isOpened: true, openedAt: new Date() },
        }));
        toastify("Invoice marked as read", "success");
      } else {
        toastify(response.message || "Failed to mark invoice as read", "error");
      }
    } catch (error) {
      toastify("Error marking invoice as read", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
  const element = document.getElementById("printable-invoice");
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const docLabel = isVoided ? "CreditNote" : "Invoice";
  const filename = `${docLabel}_${invoice?.invoiceNumber}_${timestamp}.pdf`;

    try {
      setLoading(true);
      toastify("Generating PDF...", "info");

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");

      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      toastify("PDF downloaded successfully!", "success");
    } catch (error) {
      console.error("PDF generation error:", error);
      toastify("Failed to generate PDF. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCashPaymentSuccess = () => {
    fetchInvoiceData();
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString("en-US", { month: "short" });
    const year = dateObj.getFullYear();
    const getOrdinalSuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = invoice?.currency?.code || "KES";
    const numericAmount = parseFloat(amount);
    const formattedNumber = numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currencyCode} ${formattedNumber}`;
  };

  const viewReceipt = (paymentData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (paymentData.type || "").toLowerCase();
    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
    );
    if (!isAllowedType) {
      toastify(
        "Receipt viewing is only available for cash, M-Pesa, bank transfers, and cheque payments",
        "info",
      );
      return;
    }
    navigate(`/facility/levy_management/receipt/${paymentData._id}`, {
      state: { receipt: paymentData, invoice, billerAddress },
    });
  };

  const actionBodyTemplate = (rowData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (rowData.type || "").toLowerCase();
    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
    );
    return (
      <Button
        icon="fas fa-eye"
        className="p-button-sm p-button-text"
        onClick={() => viewReceipt(rowData)}
        disabled={!isAllowedType}
        tooltip={isAllowedType ? "View Receipt" : "Receipt not available for this payment type"}
        tooltipOptions={{ position: "left" }}
      />
    );
  };

  // ─── Balance calculations ─────────────────────────────────────────────────────
  const hasBalanceBroughtForward = () =>
    invoice?.balanceBroughtForward !== 0 &&
    invoice?.balanceBroughtForward !== null &&
    invoice?.balanceBroughtForward !== undefined;

  const isCredit = invoice?.balanceBroughtForward < 0;

  const calculateTotalBalance = () => {
    if (!invoice) return 0;
    return (
      (parseFloat(invoice.totalAmount) || 0) +
      (parseFloat(invoice.balanceBroughtForward) || 0) +
      (parseFloat(invoice.penalty) || 0)
    );
  };

  const calculateTotalDue = () => {
    if (!invoice) return 0;
    return calculateTotalBalance() - (invoice.amountPaid || 0);
  };

  const isFinalCredit = calculateTotalDue() < 0;

  const formatBalanceBroughtForward = () => {
    if (!invoice?.balanceBroughtForward || invoice.balanceBroughtForward === 0)
      return formatAmount(0);
    return formatAmount(Math.abs(invoice.balanceBroughtForward));
  };

  // ─── Read/Unread styles ───────────────────────────────────────────────────────
  const readStatusStyles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem 1rem",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginLeft: "0.75rem",
    },
    unread: { backgroundColor: "#ffc107", color: "#212529" },
    read: { backgroundColor: "#28a745", color: "#ffffff" },
  };

  // ─── Loading guard ────────────────────────────────────────────────────────────
  if (loading && !invoice) {
    return (
      <Layout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "70vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading invoice details...</span>
        </div>
      </Layout>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.pageContainer}>

        {/* ── Action Bar ─────────────────────────────────────────────────────── */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link to="#" onClick={() => navigate(-1)} className={styles.backLink}>
              <i className="ti ti-arrow-narrow-left"></i>
              <span>Back</span>
            </Link>

            {/* Read / Unread status */}
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              {invoice?.viewStatus?.isOpened === true ? (
                <button style={{ ...readStatusStyles.button, ...readStatusStyles.read }}>
                  <i className="fas fa-check-circle me-1"></i>Read
                </button>
              ) : (
                <button
                  style={{ ...readStatusStyles.button, ...readStatusStyles.unread }}
                  onClick={markInvoiceAsRead}
                >
                  <i className="fas fa-exclamation-circle me-1"></i>Unread
                </button>
              )}
            </div>

            {contractLoading && (
              <div style={{ display: "inline-block", marginLeft: "20px" }}>
                <span className="text-muted">
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Loading contract details...
                </span>
              </div>
            )}
          </div>

          <div className={styles.actionCardRight}>
            {/* Pass isVoided so the dropdown can hide pay actions */}
            <InvoiceActionsDropdown
              invoice={invoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              setShowReceiptsDialog={setShowReceiptsDialog}
              onCashPaymentSuccess={handleCashPaymentSuccess}
              loading={loading}
              isVoided={isVoided}
            />
          </div>
        </div>
        {/* ── Data status messages (non-voided contract invoices only) ─────────── */}
        {!isVoided && allPaymentDataLoaded && invoice?.whatFor?.invoiceType === "Contract" && (
          <div className="mb-3">
            {!billerAddress && (
              <div className="alert alert-warning" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                No biller address configured for this invoice.
              </div>
            )}
            {!bankDetails && !paymentSettings && (
              <div className="alert alert-warning" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                No payment method configured for this invoice.
              </div>
            )}
          </div>
        )}

        {!isVoided && !allPaymentDataLoaded && invoice?.whatFor?.invoiceType === "Contract" && (
          <div className="mb-3">
            {contractLoading && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>Loading contract details...
              </div>
            )}
            {billerAddressLoading && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>Loading biller address...
              </div>
            )}
            {(bankDetailsLoading || paymentSettingsLoading) && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>Loading payment details...
              </div>
            )}
          </div>
        )}

        {/* ── Receipts Dialog ───────────────────────────────────────────────────── */}
        <Dialog
          header="Payment Receipts"
          visible={showReceiptsDialog}
          style={{ width: "50vw" }}
          onHide={() => setShowReceiptsDialog(false)}
          resizable={false}
        >
          <DataTable
            value={invoice?.reconciliationHistory || []}
            responsiveLayout="scroll"
            emptyMessage="No payment records found"
            className="p-datatable-sm"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
          >
            <Column
              field="date"
              header="Date"
              body={(rowData) => formatDate(rowData.date)}
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={(rowData) => formatAmount(rowData.amount)}
              sortable
            />
            <Column
              field="type"
              header="Type"
              sortable
              body={(rowData) => {
                const paymentType = (rowData.type || "").toLowerCase();
                const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
                const isAllowedType = allowedTypes.some(
                  (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
                );
                let displayType = rowData.type || "Unknown";
                if (paymentType === "cash") displayType = "Cash";
                else if (paymentType === "mpesa-transfer" || paymentType.includes("mpesa")) displayType = "M-Pesa";
                else if (paymentType === "bank-transfer" || paymentType.includes("bank")) displayType = "Bank Transfer";
                else if (paymentType === "cheque" || paymentType === "check") displayType = "Cheque";
                else if (paymentType === "payment") displayType = "Payment";
                return (
                  <div>
                    {displayType}
                    {isAllowedType && (
                      <i
                        className="fas fa-receipt ml-2"
                        style={{ color: "#28a745", fontSize: "0.85em", marginLeft: "5px" }}
                        title="Receipt available"
                      />
                    )}
                  </div>
                );
              }}
            />
            <Column field="paymentReference" header="Reference" sortable />
            <Column
              field="paymentCompletion"
              header="Status"
              sortable
              body={(rowData) => (
                <span
                  className={`badge ${rowData.paymentCompletion === "Completed"
                      ? "bg-success"
                      : rowData.paymentCompletion === "Partial"
                        ? "bg-warning"
                        : rowData.paymentCompletion === "Overpaid"
                          ? "bg-info"
                          : "bg-secondary"
                    }`}
                >
                  {rowData.paymentCompletion}
                </span>
              )}
            />
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "4rem" }}
            />
          </DataTable>
        </Dialog>

        {/* ── Printable Invoice ─────────────────────────────────────────────────── */}
        <div className={styles.invoiceContainer}>
          <div id="printable-invoice" className={styles.invoiceContent}>

            {/* Status banner (shows VOIDED / PAID / PENDING etc.) */}
            <div
              className={styles.statusBanner}
              data-status={invoice?.status?.toLowerCase()}
            >
              <span>{invoice?.status}</span>
            </div>

            {/* ── Invoice Header ──────────────────────────────────────────────── */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1
                    className={styles.invoiceTitleInHeader}
                    style={{
                      marginTop: "-20px",
                      marginLeft: "60%",
                      color: "white",
                      fontSize: "28px",
                      fontWeight: "600",
                    }}
                  >
                    {isVoided ? "CREDIT NOTE" : "INVOICE"}
                  </h1>
                  {facilityLogo ? (
                    <div className={styles.logoContainer} style={{ marginTop: "0", marginLeft: "60%" }}>
                      <img
                        src={facilityLogo}
                        alt="Company Logo"
                        className={styles.companyLogo}
                        onError={(e) => {
                          e.target.style.display = "none";
                          console.error("Failed to load logo:", facilityLogo);
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.logoContainer} style={{ marginTop: "0", marginLeft: "60%" }}>
                      <div className={styles.logoPlaceholder}>
                        <span>Company Logo</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>{invoice?.invoiceNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>{invoice?.accountNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.issueDate)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dueDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Invoice Body ────────────────────────────────────────────────── */}
            <div className={styles.invoiceBody}>

              {/* Address section */}
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      {billerAddress?.companyName && <div>{billerAddress.companyName}</div>}
                      {billerAddress?.address && <div>{billerAddress.address}</div>}
                      {billerAddress?.postalCode && <div>{billerAddress.postalCode}</div>}
                      {(billerAddress?.city || billerAddress?.country) && (
                        <div>
                          {billerAddress.city}
                          {billerAddress.city && billerAddress.country ? ", " : ""}
                          {billerAddress.country}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{`${invoice?.client?.firstName || "N/A"} ${invoice?.client?.lastName || ""}`}</div>
                      <div>Unit: {invoice?.unit?.name || "N/A"}</div>
                    </div>
                  </div>

                  {/* TOTAL DUE shown in address row only for non-voided invoices */}
                  {!isVoided && (
                    <div className={styles.totalSection}>
                      <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                      <div className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}>
                        {isFinalCredit
                          ? formatAmount(-Math.abs(calculateTotalDue()))
                          : formatAmount(calculateTotalDue())}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items grid */}
              <div className={styles.itemsGrid}>
                <div className={styles.gridHeader}>
                  <span className={styles.columnHeader}>DESCRIPTION</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>UNIT PRICE</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>QTY</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>AMOUNT</span>
                </div>

                {invoice?.items?.map((item, index) => (
                  <div key={index} className={styles.gridRow}>
                    <span className={styles.value}>{item.description || "N/A"}</span>
                    <span className={`${styles.value} ${styles.right}`}>{formatAmount(item.unitPrice)}</span>
                    <span className={`${styles.value} ${styles.right}`}>{item.quantity || "N/A"}</span>
                    <span className={`${styles.value} ${styles.right}`}>{formatAmount(item.quantity * item.unitPrice)}</span>
                  </div>
                ))}

                {/* ── Calculations ──────────────────────────────────────────── */}
                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>

                    {/* Subtotal */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>SUBTOTAL</span>
                      <span className={styles.calcValue}>{formatAmount(invoice?.subTotal)}</span>
                    </div>

                    {/* Tax rows */}
                    {invoice?.taxes && invoice.taxes.length > 0 ? (
                      invoice.taxes.map((taxLine, idx) => (
                        <div key={idx} className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>
                            {taxLine.name} ({taxLine.percentage}%)
                          </span>
                          <span className={styles.calcValue}>{formatAmount(taxLine.amount)}</span>
                        </div>
                      ))
                    ) : (
                      invoice?.tax > 0 && (
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>
                            TAX ({invoice?.subTotal ? ((invoice.tax / invoice.subTotal) * 100).toFixed(0) : 0}%)
                          </span>
                          <span className={styles.calcValue}>{formatAmount(invoice?.tax)}</span>
                        </div>
                      )
                    )}

                    {/* Invoice total */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>{formatAmount(invoice?.totalAmount)}</span>
                    </div>

                    {/* ── The rows below are hidden for voided invoices ──────── */}
                    {!isVoided && (
                      <>
                        <div className={styles.divider}></div>

                        {/* Balance Brought Forward */}
                        {hasBalanceBroughtForward() && (
                          <div className={styles.calculationsRow}>
                            <span className={styles.calcLabel}>BALANCE B/FORWARD</span>
                            <span
                              className={`${styles.calcValue} ${isCredit ? styles.creditAmount : styles.debitAmount}`}
                            >
                              {isCredit
                                ? "(" + formatBalanceBroughtForward() + ")"
                                : formatBalanceBroughtForward()}
                            </span>
                          </div>
                        )}

                        {/* Amount Payable */}
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>AMOUNT PAYABLE</span>
                          <span className={styles.calcValue}>{formatAmount(calculateTotalBalance())}</span>
                        </div>

                        {/* Penalty */}
                        {((invoice?.penalty && parseFloat(invoice.penalty) > 0) ||
                          (invoice?.invoiceNote && invoice.invoiceNote.includes("Penalty applied"))) && (
                            <>
                              <div className={styles.calculationsRow}>
                                <span className={styles.calcLabel}>PENALTY</span>
                                <span
                                  className={styles.calcValue}
                                  style={{ color: "#dc3545", fontWeight: "600" }}
                                >
                                  {invoice.penalty ? formatAmount(invoice.penalty) : "Applied"}
                                </span>
                              </div>
                              {invoice?.invoiceNote && invoice.invoiceNote.includes("Penalty applied") && (
                                <div
                                  className={styles.calculationsRow}
                                  style={{ fontSize: "0.85em", fontStyle: "italic" }}
                                >
                                  <span className={styles.calcLabel}></span>
                                  <span
                                    className={styles.calcValue}
                                    style={{ color: "#6c757d", fontSize: "0.85em" }}
                                  >
                                    {invoice.invoiceNote.replace("Penalty applied: ", "")}
                                  </span>
                                </div>
                              )}
                            </>
                          )}

                        <div className={styles.divider}></div>

                        {/* Payment */}
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>PAYMENT</span>
                          <span className={styles.calcValue}>{formatAmount(invoice?.amountPaid)}</span>
                        </div>

                        {/* Total Due */}
                        <div className={`${styles.calculationsRow} ${styles.balance}`}>
                          <span className={styles.calcLabel}>TOTAL DUE</span>
                          <span
                            className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                          >
                            {isFinalCredit
                              ? formatAmount(-Math.abs(calculateTotalDue()))
                              : formatAmount(calculateTotalDue())}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* ── Removed duplicate totalRow that was here ── */}
                </div>
              </div>

              {/* ── Footer: payment details — hidden entirely for voided invoices ── */}
              {isVoided ? (
                // Voided: show a neutral "void" footer instead of payment details
                <div
                  className={styles.footer}
                  style={{
                    backgroundColor: "#f8d7da",
                    padding: "12px 20px",
                    marginLeft: "-20px",
                    marginRight: "-20px",
                    marginBottom: "-20px",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    textAlign: "center",
                  }}
                >
                </div>
              ) : allPaymentDataLoaded ? (
                // Non-voided + data ready: show appropriate payment footer
                paymentMethod === "mobile" && paymentSettings ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div className={styles.paymentDetails} style={{ marginBottom: 0 }}>
                          <p style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>
                            Payment Details:
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Pay To:</strong> Mpesa
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Paybill:</strong> {paymentSettings?.shortCode || "N/A"}
                          </p>
                           <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Account No:</strong> {invoice?.accountNumber || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-5 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{ width: "120px", height: "45px", objectFit: "contain" }}
                        />
                        <p style={{ textAlign: "center", marginTop: "2px", marginBottom: "2px", fontSize: "10px" }}>
                          ___________________________
                        </p>
                        <p style={{ textAlign: "center", fontSize: "9px", marginBottom: 0 }}>
                          Authorized Signature
                        </p>
                      </div>
                      {invoice?.ticketUrl && (
                        <div className="col-sm-3 text-center">
                          <EtimsQRCode ticketUrl={invoice.ticketUrl} size={80} />
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-12 pt-1">
                        <p style={{ textAlign: "center", fontSize: "11px", marginBottom: 0 }}>
                          To complete your payment, use paybill
                          <strong> {paymentSettings?.shortCode || "N/A"} </strong>
                          with account number
                          <strong> {invoice?.accountNumber || "N/A"}</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : paymentMethod === "bank" && bankDetails ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row">
                      <div className="col-12 text-center pb-2">
                        <h6
                          className={styles.detailsTitle}
                          style={{ margin: "0 auto", fontWeight: "bold", fontSize: "12px" }}
                        >
                          PAYMENT DETAILS
                        </h6>
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <div className="col-sm-3">
                        <div className={styles.paymentDetails} style={{ marginBottom: 0 }}>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong> {bankDetails?.bankName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Account:</strong> {bankDetails?.accountName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Acc No:</strong> {bankDetails?.accountNumber || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-3">
                        <div className={styles.additionalDetails} style={{ marginBottom: 0 }}>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Branch:</strong> {bankDetails?.branchName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Code:</strong> {bankDetails?.branchCode || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Swift:</strong> {bankDetails?.bankCode || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-4 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{ width: "120px", height: "45px", objectFit: "contain" }}
                        />
                        <p style={{ marginTop: "0px", marginBottom: "2px", fontSize: "10px" }}>
                          ___________________________
                        </p>
                        <p style={{ fontSize: "9px", marginBottom: 0 }}>Authorized Signature</p>
                      </div>
                      {invoice?.ticketUrl && (
                        <div className="col-sm-2 text-center">
                          <EtimsQRCode ticketUrl={invoice.ticketUrl} size={80} />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // No payment method configured or non-contract invoice
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center justify-content-center">
                      {invoice?.whatFor?.invoiceType === "Contract" ? (
                        <div className="col-12 text-center">
                          <div className={styles.noPaymentMethod}>
                            <i
                              className="fas fa-exclamation-triangle text-warning"
                              style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}
                            ></i>
                            <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                              Payment Method Not Configured
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="col-auto text-center">
                            <p style={{ marginTop: "5px", marginBottom: "2px", fontSize: "10px" }}>
                              ___________________________
                            </p>
                            <p className="text-muted" style={{ fontSize: "9px", marginBottom: 0 }}>
                              Authorized Signature
                            </p>
                          </div>
                          {invoice?.ticketUrl && (
                            <div className="col-auto ms-4">
                              <EtimsQRCode ticketUrl={invoice.ticketUrl} size={80} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              ) : (
                // Loading state — only for contract invoices while fetching payment details
                invoice?.whatFor?.invoiceType === "Contract" && (
                  <div
                    className={styles.footer}
                    style={{ backgroundColor: "#f8f9fa", borderTop: "2px solid #dee2e6" }}
                  >
                    <div className="row px-5 py-4">
                      <div className="col-12 text-center">
                        <div className={styles.loadingPaymentDetails}>
                          <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="text-muted">Loading Payment Details</h5>
                          <p className="text-muted mb-0">
                            Please wait while we fetch the payment information...
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InvoicePage;